/**
 * The vocabulary of NATO map symbols the group already uses in its missions.
 *
 * A mission made in Eden marks its map with the engine's own icons: `b_inf`,
 * `o_motor_inf`, `n_installation`, `u_unknown` and the plain `mil_*` family.
 * Those icons live in the game's data, which a web page cannot read, so the
 * same vocabulary is drawn here as APP-6 shapes: the frame says whose it is
 * (friendly a rectangle, hostile a diamond, neutral a square, unknown a
 * quatrefoil) and the glyph inside says what it is. What the editor picks is
 * stored as the engine's own class name and reaches `setMarkerType` unchanged,
 * so what a player sees in game is the real icon and what an editor sees here
 * is the same symbol.
 *
 * Adding a symbol is adding a row to ROLES; adding a whole family is adding a
 * row to SIDES. The drawing is `NatoSymbol.tsx`; what a symbol means and what it
 * is called is here, so a picker, a legend and a test can read it without React.
 */

/** The four framings of APP-6, in the order the picker shows them. */
export const SIDES = [
  { key: 'b', label: 'Amic', colour: 'ColorWEST' },
  { key: 'o', label: 'Hostil', colour: 'ColorEAST' },
  { key: 'n', label: 'Neutral', colour: 'ColorGUER' },
  { key: 'u', label: 'Desconegut', colour: 'ColorUNKNOWN' },
] as const;

export type SideKey = (typeof SIDES)[number]['key'];

/** The roles the engine has an icon for, with the Catalan name the picker shows. */
export const ROLES = [
  { key: 'inf', label: 'Infanteria' },
  { key: 'motor_inf', label: 'Infanteria motoritzada' },
  { key: 'mech_inf', label: 'Infanteria mecanitzada' },
  { key: 'armor', label: 'Blindats' },
  { key: 'recon', label: 'Reconeixement' },
  { key: 'art', label: 'Artilleria' },
  { key: 'mortar', label: 'Morters' },
  { key: 'antiair', label: 'Antiaeri' },
  { key: 'air', label: 'Helicòpters' },
  { key: 'plane', label: 'Avions' },
  { key: 'uav', label: 'Dron' },
  { key: 'naval', label: 'Naval' },
  { key: 'med', label: 'Sanitat' },
  { key: 'service', label: 'Serveis' },
  { key: 'support', label: 'Suport' },
  { key: 'maint', label: 'Manteniment' },
  { key: 'hq', label: 'Comandament' },
  { key: 'installation', label: 'Instal·lació' },
  { key: 'unknown', label: 'Sense identificar' },
] as const;

export type RoleKey = (typeof ROLES)[number]['key'];

/**
 * The plain marks a mission uses beside the framed symbols: the game's own
 * seventeen, and only those.
 *
 * This list is `\a3\ui_f\data\map\markers\military\` read out of the game's own
 * `ui_f_data.pbo` — every one of them is a marker Arma ships, drawn on the map
 * and in the designer with the game's own picture. A name the game does not
 * have is a mark that can only ever be our drawing of it, which is what
 * `mil_defend` was until 2026-09-09: the one entry here nobody could see the
 * real version of, because there is no real version. Do not add one back
 * without checking it against that folder; the fallback drawing is for a panel
 * that is not answering, not for inventing marks.
 */
export const PLAIN = [
  { key: 'mil_dot', label: 'Punt' },
  { key: 'mil_objective', label: 'Objectiu' },
  { key: 'mil_marker', label: 'Marca' },
  { key: 'mil_start', label: 'Inici' },
  { key: 'mil_end', label: 'Final' },
  { key: 'mil_join', label: 'Reunió' },
  { key: 'mil_warning', label: 'Avís' },
  { key: 'mil_destroy', label: 'Destruir' },
  { key: 'mil_pickup', label: 'Recollida' },
  { key: 'mil_ambush', label: 'Emboscada' },
  { key: 'mil_flag', label: 'Bandera' },
  { key: 'mil_box', label: 'Caixa' },
  { key: 'mil_circle', label: 'Cercle' },
  { key: 'mil_triangle', label: 'Triangle' },
  { key: 'mil_arrow', label: 'Fletxa' },
  { key: 'mil_arrow2', label: 'Fletxa plena' },
  { key: 'mil_unknown', label: 'Interrogant' },
] as const;

/** Arma's CfgMarkerColors, as the browser draws them. */
export const MARKER_COLOURS: Record<string, string> = {
  ColorBlack: '#101010',
  ColorGrey: '#808080',
  ColorRed: '#e51400',
  ColorBrown: '#7f4000',
  ColorOrange: '#faa600',
  ColorYellow: '#f2f200',
  ColorKhaki: '#999959',
  ColorGreen: '#00cc00',
  ColorBlue: '#004c99',
  ColorPink: '#ff5999',
  ColorWhite: '#f2f2f2',
  ColorWEST: '#00478f',
  ColorEAST: '#8c0000',
  ColorGUER: '#00801a',
  ColorCIV: '#66008c',
  ColorUNKNOWN: '#b39900',
};

/** The colours the pickers offer, in the order a mission tends to use them. */
export const COLOUR_CHOICES = [
  'ColorWEST',
  'ColorEAST',
  'ColorGUER',
  'ColorCIV',
  'ColorUNKNOWN',
  'ColorBlack',
  'ColorRed',
  'ColorOrange',
  'ColorYellow',
  'ColorGreen',
  'ColorBlue',
  'ColorWhite',
  'ColorGrey',
  'ColorPink',
];

export function colourOf(name: string | undefined): string {
  return MARKER_COLOURS[name ?? ''] ?? MARKER_COLOURS.ColorBlack;
}

/** `b_motor_inf` → `{ side: 'b', role: 'motor_inf' }`; anything else is plain. */
export function parseSymbol(symbol: string): { side: SideKey; role: string } | null {
  const match = /^([bonu])_(.+)$/.exec(symbol);
  if (!match) return null;
  return { side: match[1] as SideKey, role: match[2] };
}

export function symbolLabel(symbol: string): string {
  const parsed = parseSymbol(symbol);
  if (parsed) {
    const side = SIDES.find((s) => s.key === parsed.side)?.label ?? parsed.side;
    const role = ROLES.find((r) => r.key === parsed.role)?.label ?? parsed.role;
    return `${role} · ${side}`;
  }
  return PLAIN.find((p) => p.key === symbol)?.label ?? symbol;
}

/** The side a symbol implies, for the colour a new marker takes by default. */
export function defaultColour(symbol: string): string {
  const parsed = parseSymbol(symbol);
  if (!parsed) return 'ColorBlack';
  return SIDES.find((s) => s.key === parsed.side)?.colour ?? 'ColorBlack';
}
